import _ from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { usePipedriveStore } from "../..";
import useNavigateToDeal from "../../hooks/useNavigateToDeal";
import usePortfolioForDeal from "../../hooks/usePortfolioForDeal";
import inputStyles from "../inputs/Input.module.css";
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import { formatDateTime, formatNumber, formatPrice, isImageFile } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import EntitiesDelete from "../EntitiesDelete";
import AttachmentFile from "../AttachmentFile";
import AttachmentImage from "../AttachmentImage";
import FieldRow from "../FieldRow";
import GalleryProvider from "../GalleryProvider";
import FileInput from "../inputs/FileInput";
import Note from "../Note";
import Email from "../Email";
import AddDealNote from "./AddDealNote";
import styles from "./DealDetails.module.css";
import { apiUrl } from "../../api/config";

function DealDetailsDetailsTab() {
    const { id } = useParams();
    const pipedriveStore = usePipedriveStore();
    const deal = useMemo(() => computed(() => pipedriveStore.deals.find(d => d.id.toString() === id)!), [id]).get();
    const labelOption = useMemo(() => computed(() => pipedriveStore.labelOptions.find(o => o.id === deal.labelId)), [deal]).get();
    const contactPipedriveOrganization = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === deal.contactCompanyId)), [deal]).get();
    const propertyOwnerPipedrivePerson = useMemo(() => computed(() => pipedriveStore.persons.find(p => p.id === deal.propertyOwnerId)), [deal]).get();
    const brokerPipedrivePerson = useMemo(() => computed(() => pipedriveStore.persons.find(p => p.id === deal.brokerId)), [deal]).get();
    const contactPipedrivePerson = useMemo(() => computed(() => pipedriveStore.persons.find(p => p.id === deal.contactPersonId)), [deal]).get();
    const assignedToUser = useMemo(() => computed(() => pipedriveStore.users.find(p => p.id === deal.assignedToUserId)), [deal]).get();
    const images = useMemo(() => computed(() => (pipedriveStore.dealIdsFileLists[deal.id] || []).filter(f => isImageFile(f.name))), [deal]).get();
    const files = useMemo(() => computed(() => (pipedriveStore.dealIdsFileLists[deal.id] || []).filter(f => !isImageFile(f.name))), [deal]).get();
    const [fileIdForDelete, setFileIdForDelete] = useState<number | null>(null);
    const bodyContainerClassName = useBodyContainerClassName();
    const { portfolio, deals: portfolioDeals } = usePortfolioForDeal(deal.id);
    const navigateToDeal = useNavigateToDeal();

    const timeline = useMemo(() => computed(() => _.orderBy([
        ...(pipedriveStore.dealIdsNoteLists[deal.id] || []).map(note => ({ type: "note" as const, date: note.createdAt, note })),
        ...(pipedriveStore.dealIdsMailMessageLists[deal.id] || []).map(mailMessage => ({ type: "email" as const, date: mailMessage.messageTime, mailMessage })),
    ], item => item.date, "desc")), [deal]).get();

    useEffect(() => {
        if (!pipedriveStore.dealIdsNoteLists[deal.id])
            pipedriveStore.fetchDealNotes(deal.id);
        if (!pipedriveStore.dealIdsMailMessageLists[deal.id])
            pipedriveStore.fetchDealMailMessages(deal.id);
    }, [deal]);

    return (
        <div className={bodyContainerClassName}>
            <div className={detailsStyles.columns}>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    {portfolio && (
                        <div className={inputStyles.container}>
                            <label className={commonStyles.header}>Portfolio</label>
                            <select
                                value={deal.id}
                                onChange={e => navigateToDeal(Number(e.target.value), true)}
                            >
                                {portfolioDeals.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className={commonStyles.header}>Deal</div>
                    <FieldRow label="ADDRESS">
                        {deal.address}
                    </FieldRow>
                    <FieldRow label="ASKING PRICE">
                        {deal.askingPrice && formatPrice(deal.askingPrice)}
                    </FieldRow>
                    <FieldRow label="BUILDING SIZE">
                        {deal.buildingSize && `${formatNumber(deal.buildingSize)} Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="LAND SIZE">
                        {deal.landSizeAcres && `${formatNumber(deal.landSizeAcres)} Acres`}
                    </FieldRow>
                    <FieldRow label="USABLE LAND SIZE">
                        {deal.usableLand && `${formatNumber(deal.usableLand)} Acres`}
                    </FieldRow>
                    <FieldRow label="ZONING">
                        {deal.zoning && deal.zoning}
                    </FieldRow>
                    <FieldRow label="LABEL">
                        {labelOption && labelOption.label}
                    </FieldRow>
                    <FieldRow label="DEAL PROFILE">
                        {deal.dealProfile && deal.dealProfile}
                    </FieldRow>
                    <FieldRow label="SOURCING METHOD">
                        {deal.sourcingMethod && deal.sourcingMethod}
                    </FieldRow>
                    <FieldRow label="PARCEL ID">
                        {deal.parcelId && deal.parcelId}
                    </FieldRow>
                    <FieldRow label="LAST MODIFIED">
                        <span>{formatDateTime(deal.updatedAt)}</span>
                    </FieldRow>
                </div>
                <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                    <div className={commonStyles.header}>Contacts</div>
                    <FieldRow label="PROPERTY OWNER">
                        {propertyOwnerPipedrivePerson && <a target="_blank" className={commonStyles.link} href={pipedriveStore.getLinkToPerson(propertyOwnerPipedrivePerson.id)}>{propertyOwnerPipedrivePerson.name}</a>}
                    </FieldRow>
                    <FieldRow label="BROKER">
                        {brokerPipedrivePerson && <a target="_blank" className={commonStyles.link} href={pipedriveStore.getLinkToPerson(brokerPipedrivePerson.id)}>{brokerPipedrivePerson.name}</a>}
                    </FieldRow>
                    <FieldRow label="CONTACT NAME">
                        {contactPipedrivePerson && <a target="_blank" className={commonStyles.link} href={pipedriveStore.getLinkToPerson(contactPipedrivePerson.id)}>{contactPipedrivePerson.name}</a>}
                    </FieldRow>
                    <FieldRow label="CONTACT COMPANY">
                        {contactPipedriveOrganization && <a target="_blank" className={commonStyles.link} href={pipedriveStore.getLinkToOrganization(contactPipedriveOrganization.id)}>{contactPipedriveOrganization.name}</a>}
                    </FieldRow>
                    <FieldRow label="TENANT">
                        {deal.tenant && deal.tenant}
                    </FieldRow>
                    <FieldRow label="ASSIGNED TO">
                        {assignedToUser && assignedToUser.name}
                    </FieldRow>
                </div>
                <div className={detailsStyles.column}>
                    <div className={commonStyles.header}>Attachments</div>
                    {!!images.length && (
                        <div className={styles.attachmentImages}>
                            <GalleryProvider>
                                {images.map(image => (
                                    <AttachmentImage
                                        key={image.id}
                                        url={`${apiUrl}/proxy/image?url=${encodeURIComponent(image.downloadUrl)}`}
                                        onDeleteClick={() => setFileIdForDelete(image.id)}
                                    />
                                ))}
                            </GalleryProvider>
                        </div>
                    )}
                    <div className={styles.attachmentFiles}>
                        {files.map(file => (
                            <AttachmentFile
                                key={file.id}
                                name={file.name}
                                url={file.url}
                                updatedAt={file.updatedAt}
                                size={file.fileSize}
                                onDeleteClick={() => setFileIdForDelete(file.id)}
                            />
                        ))}
                    </div>
                    <FileInput onFilesSelected={files => files.forEach(file => pipedriveStore.createDealFile(file, file.name, deal.id))} />
                    {fileIdForDelete && (
                        <EntitiesDelete
                            entityCount={1}
                            entityName={"File"}
                            onCancel={() => setFileIdForDelete(null)}
                            onDelete={() => {
                                pipedriveStore.deleteDealFile(fileIdForDelete, deal.id);
                                setFileIdForDelete(null);
                            }}
                        />
                    )}
                </div>
            </div>
            <div className={detailsStyles.columns}>
                <div className={detailsStyles.column}>
                    <div className={commonStyles.header}>Comments</div>
                    <AddDealNote dealId={deal.id} />
                    {timeline.map(item => item.type === "note"
                        ? (
                            <Note
                                key={`note-${item.note.id}`}
                                userName={pipedriveStore.users.find(u => u.id === item.note.userId)?.name}
                                text={item.note.content}
                                createdAt={item.note.createdAt}
                            />
                        )
                        : (
                            <Email
                                key={`email-${item.mailMessage.id}`}
                                subject={item.mailMessage.subject}
                                from={item.mailMessage.from}
                                to={item.mailMessage.to}
                                snippet={item.mailMessage.snippet}
                                body={item.mailMessage.body}
                                hasBodyFlag={item.mailMessage.hasBodyFlag}
                                messageTime={item.mailMessage.messageTime}
                                onExpand={() => pipedriveStore.fetchDealMailMessageBody(deal.id, item.mailMessage.id)}
                            />
                        ))}
                </div>
            </div>
        </div>
    );
}

export default observer(DealDetailsDetailsTab);
